import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'shell/Dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { getFtpStreamUrl } from '@/services/api';
import { customToast } from '@/components/ui/custom-toast';
import { Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PreviewCard } from './PreviewCard';
import { SelectionPanel } from './SelectionPanel';
import {
  EditEpisodeDetailsDialogProps,
  TitleOption,
  DescriptionOption,
  RightPanelState,
} from './types';

export const EditEpisodeDetailsDialog: React.FC<EditEpisodeDetailsDialogProps> = ({
  open,
  onOpenChange,
  episode,
  clips,
  onSave,
}) => {
  // Form state
  const [title, setTitle] = useState(episode.title || '');
  const [description, setDescription] = useState(episode.description || '');
  const [originalTitle] = useState(episode.title || '');
  const [originalDescription] = useState(episode.description || '');

  // AI suggestion options
  const [titleOptions, setTitleOptions] = useState<TitleOption[]>([]);
  const [descriptionOptions, setDescriptionOptions] = useState<DescriptionOption[]>([]);

  // Right panel state machine
  const [rightPanelState, setRightPanelState] = useState<RightPanelState>('initial');

  // Loading states
  const [isSaving, setIsSaving] = useState(false);

  // Artwork URL
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);

  // Refs for focusing inputs in manual edit mode
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  const MAX_DESCRIPTION_LENGTH = 175;
  const WARNING_THRESHOLD = Math.floor(MAX_DESCRIPTION_LENGTH * 0.9);

  // Track suggestions status from episode
  const suggestionsStatus = episode.suggestionsStatus;

  // Track if we initiated the current generation
  const [generationInitiated, setGenerationInitiated] = useState(false);

  // Check if suggestions are available
  const hasGeneratedOptions = titleOptions.length > 0 || descriptionOptions.length > 0;

  // Handle suggestions arriving via WebSocket
  useEffect(() => {
    if (suggestionsStatus === 'ready') {
      // Go to title selection whenever suggestions become ready
      setRightPanelState('selecting-title');
      setGenerationInitiated(false);
    } else if (generationInitiated && suggestionsStatus === 'failed') {
      setRightPanelState('initial');
      setGenerationInitiated(false);
      customToast.error('Failed to generate suggestions');
    }
  }, [suggestionsStatus, generationInitiated]);

  // Load suggestions when they arrive
  useEffect(() => {
    if (episode.suggestedTitles && episode.suggestedTitles.length > 0) {
      setTitleOptions(
        episode.suggestedTitles.map((text, index) => ({
          id: `title-${index}`,
          text,
        }))
      );
    }
    if (episode.suggestedDescriptions && episode.suggestedDescriptions.length > 0) {
      setDescriptionOptions(
        episode.suggestedDescriptions.map((text, index) => ({
          id: `desc-${index}`,
          text,
        }))
      );
    }
  }, [episode.suggestedTitles, episode.suggestedDescriptions]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(episode.title || '');
      setDescription(episode.description || '');

      // Determine initial right panel state
      if (episode.suggestedTitles?.length || episode.suggestedDescriptions?.length) {
        setRightPanelState('generated');
      } else {
        setRightPanelState('initial');
      }
    }
  }, [open, episode.title, episode.description, episode.suggestedTitles, episode.suggestedDescriptions]);

  // Load artwork URL
  useEffect(() => {
    const loadArtworkUrl = async () => {
      if (!episode.coverArtUrl) {
        setArtworkUrl(null);
        return;
      }

      if (
        episode.coverArtUrl.startsWith('http://') ||
        episode.coverArtUrl.startsWith('https://')
      ) {
        setArtworkUrl(episode.coverArtUrl);
        return;
      }

      try {
        const url = await getFtpStreamUrl(episode.coverArtUrl, episode.companyId);
        setArtworkUrl(url);
      } catch (err) {
        console.error('Failed to get artwork URL:', err);
        setArtworkUrl(null);
      }
    };

    loadArtworkUrl();
  }, [episode.coverArtUrl, episode.companyId]);

  const handleGenerateSuggestions = useCallback(async () => {
    if (clips.length === 0) {
      customToast.info('Add clips to your episode', {
        description: 'Then we can generate suggested titles and descriptions.',
      });
      return;
    }

    setRightPanelState('generating');
    setGenerationInitiated(true);
    try {
      await PodcastEpisodeService.generateSuggestions(episode.id);
      customToast.success('Generating suggestions...', {
        description: 'This may take a moment.',
      });
    } catch (error) {
      customToast.error('Failed to generate suggestions', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      setRightPanelState('initial');
      setGenerationInitiated(false);
    }
  }, [episode.id, clips.length]);

  const handleViewGenerated = () => {
    setRightPanelState('selecting-title');
  };

  const handleEditManually = () => {
    setRightPanelState('manual-edit');
    setTimeout(() => {
      titleInputRef.current?.focus();
    }, 100);
  };

  const handleTitleSelect = (text: string) => {
    setTitle(text);
    // Auto-advance to description selection
    setRightPanelState('selecting-description');
  };

  const handleDescriptionSelect = (text: string) => {
    setDescription(text);
    // Close panel, back to generated state
    setRightPanelState('generated');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(value);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      customToast.error('Title is required');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(title.trim(), description.trim());
      onOpenChange(false);
    } catch (error) {
      customToast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getCharCountColor = () => {
    if (description.length >= MAX_DESCRIPTION_LENGTH) return 'text-red-500';
    if (description.length >= WARNING_THRESHOLD) return 'text-yellow-500';
    return 'text-muted-foreground';
  };

  const canSave = title.trim().length > 0;

  // Render right panel content based on state
  const renderRightPanelContent = () => {
    switch (rightPanelState) {
      case 'initial':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Sparkles className="h-12 w-12 text-cyan-500 mb-6" />
            <p className="text-[var(--pm-text-faint)] mb-6 max-w-xs">
              Generate 5 title options and 3 description options based on your episode content.
            </p>
            <Button
              variant="outline"
              onClick={handleGenerateSuggestions}
              className="border-cyan-500 text-cyan-500 hover:bg-cyan-500/10 mb-4"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate with AI
            </Button>
            <span className="text-[var(--pm-text-faint)] text-sm mb-2">or</span>
            <button
              onClick={handleEditManually}
              className="text-cyan-500/70 text-sm underline hover:text-cyan-500 transition-colors"
            >
              edit manually
            </button>
          </div>
        );

      case 'generating':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Loader2 className="h-12 w-12 text-cyan-500 mb-6 animate-spin" />
            <p className="text-[var(--pm-text-faint)] mb-6">
              Generating suggestions...
            </p>
          </div>
        );

      case 'generated':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Sparkles className="h-12 w-12 text-cyan-500 mb-6" />
            <p className="text-[var(--pm-text-faint)] mb-6 max-w-xs">
              Your title options and description options were generated and are available.
            </p>
            <Button
              variant="outline"
              onClick={handleViewGenerated}
              className="border-cyan-500 text-cyan-500 hover:bg-cyan-500/10 mb-4"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              View Generated
            </Button>
            <span className="text-[var(--pm-text-faint)] text-sm mb-2">or</span>
            <button
              onClick={handleEditManually}
              className="text-cyan-500/70 text-sm underline hover:text-cyan-500 transition-colors"
            >
              edit manually
            </button>
          </div>
        );

      case 'manual-edit':
        return (
          <div className="flex flex-col h-full px-6 py-4">
            <div className="space-y-4 flex-1">
              {/* Title Input */}
              <div className="space-y-2">
                <Label htmlFor="episode-title">Episode Title</Label>
                <Textarea
                  ref={titleInputRef}
                  id="episode-title"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter episode title..."
                  maxLength={255}
                  rows={2}
                  className="resize-none"
                />
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <Label htmlFor="episode-description">Episode Description</Label>
                <Textarea
                  ref={descriptionInputRef}
                  id="episode-description"
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Describe what this episode covers..."
                  rows={6}
                  className="resize-none"
                />
                <div className="flex justify-end">
                  <span className={cn('text-xs', getCharCountColor())}>
                    {description.length} / {MAX_DESCRIPTION_LENGTH}
                  </span>
                </div>
              </div>
            </div>

            {/* Back to AI options if available */}
            {hasGeneratedOptions && (
              <div className="pt-4 border-t border-[var(--pm-border-default)] text-center">
                <button
                  onClick={() => setRightPanelState('generated')}
                  className="text-cyan-500/70 text-sm underline hover:text-cyan-500 transition-colors"
                >
                  back to AI options
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Wrapper for relative positioning of SelectionPanel */}
        <div className="relative flex flex-col flex-1 overflow-hidden -m-6">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Edit Episode Details</DialogTitle>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-hidden px-6">
            <div className="flex h-[580px]">
              {/* Left Panel - Preview Card */}
              <div className="w-1/2 flex-shrink-0 pr-6 overflow-y-auto">
                <PreviewCard
                  title={title}
                  description={description}
                  artworkUrl={artworkUrl}
                  episodeDate={episode.createdAt instanceof Date ? episode.createdAt.toISOString() : undefined}
                />
              </div>

              {/* Right Panel - Action Area */}
              <div className="w-1/2 relative overflow-hidden border-l border-[var(--pm-border-default)]">
                {renderRightPanelContent()}
              </div>
            </div>
          </div>

          <div className="p-6 pt-0">
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !canSave}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </div>

          {/* Selection Panels - positioned relative to wrapper */}
          <SelectionPanel
            isOpen={rightPanelState === 'selecting-title'}
            type="title"
            options={titleOptions}
            currentValue={originalTitle}
            onSelect={handleTitleSelect}
            onEditManually={handleEditManually}
          />
          <SelectionPanel
            isOpen={rightPanelState === 'selecting-description'}
            type="description"
            options={descriptionOptions}
            currentValue={originalDescription}
            onSelect={handleDescriptionSelect}
            onEditManually={handleEditManually}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
